import { useState } from 'react';
import { del, errorText, put } from './api.js';
import { Link, navigate } from './router.jsx';
import { useAuth } from './session.jsx';
import { t } from './i18n.js';
import { Icon, L, PriceTag, areaText, floorText, money, priceText, useToast, stateText } from './ui.jsx';

// Bookmark toggle shared by every listing card. Guests are sent to sign in and come back.
export function BookmarkButton({ p, className = 'bm' }) {
  const { user } = useAuth();
  const toast = useToast();
  const [on, setOn] = useState(!!p.is_favorited);
  if (user?.role === 'admin') return null;
  const toggle = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) return navigate(`/login?next=${encodeURIComponent(location.pathname + location.search)}`);
    try { on ? await del(`/v1/favorites/${p.id}`) : await put(`/v1/favorites/${p.id}`); setOn(!on); toast(on ? t('นำออกจากรายการที่บันทึกแล้ว', 'Removed from saved') : t('บันทึกประกาศแล้ว', 'Listing saved')); }
    catch (er) { toast(errorText(er)); }
  };
  return <button type="button" className={className} aria-pressed={on} aria-label={on ? t('นำออกจากรายการที่บันทึก', 'Remove from saved') : t('บันทึกประกาศ', 'Save listing')} onClick={toggle}>{on ? Icon.bookmarkOn : Icon.bookmark}</button>;
}

// Trust signals. A card shows at most two badges on the photo: ownership checked, and confirmed today.
// A paid "featured" mark stays small and off the photo, so verification is always more visible than promotion.
export const FeaturedBadge = () => <span className="badge pri"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m12 3.5 2.6 5.4 5.9.8-4.3 4.1 1.1 5.9L12 16.9l-5.3 2.8 1.1-5.9L3.5 9.700l5.9-.8z" /></svg>{t('แนะนำ', 'Featured')}</span>;

const daysSince = (iso) => Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 86400000));
export function freshnessText(fr) {
  if (!fr) return '';
  if (fr.status === 'paused') return t('หยุดชั่วคราว', 'Temporarily paused');
  if (fr.status === 'fresh') return t('ยืนยันวันนี้', 'Confirmed today');
  if (fr.status === 'recent') { const d = daysSince(fr.confirmed_at); return t(`ยืนยันเมื่อ ${d} วันก่อน`, `Confirmed ${d} day${d === 1 ? '' : 's'} ago`); }
  return t('รอผู้ลงประกาศยืนยัน', 'Needs confirmation');
}
export function sourceText(src) {
  if (!src) return '';
  const name = { owner: t('เจ้าของโดยตรง', 'Direct owner'), agent: t('นายหน้า', 'Agent'), agency: t('บริษัทนายหน้า', 'Agency') }[src.type];
  return src.verified ? t(`${name} ยืนยันตัวตนแล้ว`, `${name}, identity verified`) : name;
}
export function responseText(r) {
  const m = r?.typical_response_minutes;
  if (!m) return '';
  if (m < 60) return t(`มักตอบภายใน ${m} นาที`, `Usually responds in ${m} min`);
  const h = Math.round(m / 60);
  return h < 24 ? t(`มักตอบภายใน ${h} ชั่วโมง`, `Usually responds in ${h} h`) : t('มักตอบภายใน 1 วันขึ้นไป', 'Usually responds in a day or more');
}
export const Freshness = ({ fr }) => (fr ? <span className={`fresh ${fr.status}`}><i aria-hidden="true" />{freshnessText(fr)}</span> : null);

export function ListingBadge({ p }) {
  return <>
    {p.verified_badge && <span className="badge ver">{Icon.verified}{t('ตรวจเอกสารสิทธิ์แล้ว', 'Ownership checked')}</span>}
    {p.freshness?.status === 'fresh' && <span className="badge fresh">{t('ยืนยันวันนี้', 'Confirmed today')}</span>}
  </>;
}

export const specs = (p) => [
  p.bedrooms != null && [Icon.bed, t(`${p.bedrooms} นอน`, `${p.bedrooms} bed`)],
  p.bathrooms != null && [Icon.bath, t(`${p.bathrooms} น้ำ`, `${p.bathrooms} bath`)],
  p.floor_level != null && [Icon.condo, floorText(p)],
  p.area_value != null && [Icon.area, areaText(p)],
  p.nearby_transit && [Icon.train, p.nearby_transit],
].filter(Boolean);

// Short price per area for cards, e.g. ฿446/ตร.ม. Not shown for เซ้ง or ขายดาวน์, whose price is not for the space itself.
export function perAreaShort(p) {
  if (!p.price || !p.area_value || ['lease_transfer', 'down_payment'].includes(p.listing_type)) return '';
  return `${money(Math.round(p.price / Number(p.area_value)))}/${L.area_unit[p.area_unit] || ''}`;
}

// Image first, then price, what and where, key facts, and who is behind it.
export function ListingCard({ p, showStatus = false }) {
  const where = [L.property_type[p.property_type], p.project_name, p.city, stateText(p.state)].filter(Boolean).join(' · ');
  const facts = [
    p.bedrooms != null && [Icon.bed, t(`${p.bedrooms} นอน`, `${p.bedrooms} bd`), t('ห้องนอน', 'Bedrooms')],
    p.bathrooms != null && [Icon.bath, t(`${p.bathrooms} น้ำ`, `${p.bathrooms} ba`), t('ห้องน้ำ', 'Bathrooms')],
    p.floor_level != null && [Icon.condo, floorText(p), t('ชั้น', 'Floor')],
    p.area_value != null && [Icon.area, areaText(p), t('พื้นที่', 'Area')],
  ].filter(Boolean);
  const reply = responseText(p.response);
  const per = perAreaShort(p);
  return (
    <article className={`rcard${p.featured ? ' feat' : ''}`}>
      <div className="ph">
        {p.cover_photo_url && <img src={p.cover_photo_url} alt="" loading="lazy" />}
        <div className="badges">{showStatus && p.status !== 'published' ? <span className="badge gold">{L.status[p.status]}</span> : <ListingBadge p={p} />}</div>
        <BookmarkButton p={p} className="bm glass" />
        {p.photo_count > 1 && <span className="pcount num" aria-label={t(`${p.photo_count} รูป`, `${p.photo_count} photos`)}>{Icon.camera}{p.photo_count}</span>}
      </div>
      <div className="bd">
        <div className="row between" style={{ flexWrap: 'nowrap', gap: 6 }}>
          <span className="price num"><PriceTag p={p} /></span>
          {p.featured && <FeaturedBadge />}
        </div>
        {per && <span className="ppa num">{per}</span>}
        <Link className="t" to={`/p/${p.id}`}>{p.title || t('ยังไม่ได้ตั้งหัวข้อ', 'No title yet')}</Link>
        <span className="sub loc">{Icon.pin}<span>{where}</span></span>
        {facts.length > 0 && <span className="specs num">{facts.map(([icon, text, label]) => <span key={label} title={label}>{icon}{text}</span>)}</span>}
        {p.nearby_transit && <span className="transit">{Icon.train}{p.nearby_transit}</span>}
        <div className="trustrow">
          {p.freshness?.status !== 'fresh' && <Freshness fr={p.freshness} />}
          <span className="src">{p.source?.verified && Icon.verified}{sourceText(p.source)}</span>
          {reply && <span className="src">{reply}</span>}
        </div>
      </div>
    </article>
  );
}

export function HeroCard({ p }) {
  return (
    <article className="hero">
      <div className="ph">
        {p.cover_photo_url && <img src={p.cover_photo_url} alt={p.title} />}
        <div className="tl"><span className="row" style={{ gap: 6 }}><ListingBadge p={p} /></span><BookmarkButton p={p} className="glass" /></div>
        <span className="tag num"><PriceTag p={p} /></span>
      </div>
      <div className="bd">
        <div className="stack" style={{ gap: 4 }}>
          <span className="cap">{[L.listing_type[p.listing_type], p.city, stateText(p.state)].filter(Boolean).join(' • ')}</span>
          <h3><Link to={`/p/${p.id}`}>{p.title}</Link></h3>
        </div>
        <div className="specrow num">{specs(p).map(([icon, text]) => <span key={text}>{icon}{text}</span>)}</div>
        <div className="trustrow onhero">{p.freshness?.status !== 'fresh' && <Freshness fr={p.freshness} />}<span className="src">{p.source?.verified && Icon.verified}{sourceText(p.source)}</span></div>
        <div className="acts">
          <Link className="btn light" to={`/p/${p.id}?contact=viewing`}>{Icon.calendar}{t('นัดชมทรัพย์', 'Book a viewing')}</Link>
          <Link className="sq" to={`/p/${p.id}?contact=message`} aria-label={t('ฝากข้อความถึงผู้ลงประกาศ', 'Leave a message for the lister')}>{Icon.chat}</Link>
        </div>
      </div>
    </article>
  );
}

// Text for LINE groups and Facebook posts, with a link back to the listing.
export function listingShareText(p) {
  const facts = [p.bedrooms != null && t(`${p.bedrooms} ห้องนอน`, `${p.bedrooms} bed`), p.bathrooms != null && t(`${p.bathrooms} ห้องน้ำ`, `${p.bathrooms} bath`), areaText(p)].filter(Boolean).join(' ');
  return [
    `${L.listing_type[p.listing_type]} ${L.property_type[p.property_type]} ${p.title || ''}`.trim(),
    `${t('ราคา', 'Price')} ${priceText(p)}`,
    facts, [p.project_name, p.city, stateText(p.state)].filter(Boolean).join(' '), p.nearby_transit,
    `${t('ดูรูปและติดต่อผู้ลงประกาศ', 'Photos and contact')}: ${location.origin}/p/${p.id}`,
  ].filter(Boolean).join('\n');
}
export async function shareListing(p, toast) {
  const text = listingShareText(p);
  try {
    if (navigator.share) { await navigator.share({ title: p.title, text, url: `${location.origin}/p/${p.id}` }); return; }
    await navigator.clipboard.writeText(text); toast(t('คัดลอกข้อความแล้ว วางใน LINE หรือ Facebook ได้เลย', 'Copied. Paste it into LINE or Facebook'));
  } catch (e) { if (e?.name !== 'AbortError') toast(t('คัดลอกไม่ได้ ลองอีกครั้ง', 'Could not copy. Try again')); }
}
